import { SOUND_ACTIONS, SOUND_GROUPS, SOUND_MAX_BYTES, soundAction, type SoundAction, type SoundInfo } from '@tanky/shared';
import { api } from '../api';
import { generateDefaults, generateDefaultSound, hasDefaultSound } from '../audio/defaultSounds';
import type { OutputMode } from '../audio/engine';
import { CH } from '../audio/pan';
import { h, toast } from '../dom';
import { go, session, type Screen } from '../session';
import { sound } from '../sound';

const OUTPUT_LABEL: Record<OutputMode, string> = { auto: 'Automaticky', surround: '5.1', stereo: 'Stereo' };

function channelsBadge(n: number): HTMLElement {
  const label = n >= 6 ? '5.1' : n === 2 ? 'stereo' : n === 1 ? 'mono' : `${n} kan.`;
  return h('span', { class: `badge${n >= 6 ? ' good' : ''}` }, label);
}

function fmtDuration(s: number): string {
  return s < 10 ? `${s.toFixed(1)} s` : `${Math.round(s)} s`;
}

/** Stránka Zvuky: výstup 5.1, herní akce a přímé přiřazení/nahrávání zvuků. */
export function soundsScreen(): Screen {
  const engine = sound.engine;
  const lib = engine.library;
  const admin = session.isAdmin;
  const body = h('div', { class: 'stack' });

  // ---------- Výstup ----------
  function outputCard(): HTMLElement {
    engine.audio();
    const max = engine.audio()?.destination.maxChannelCount ?? 2;
    const select = h(
      'select',
      {
        onchange: (e: Event) => {
          sound.setOutput((e.target as HTMLSelectElement).value as OutputMode);
          render();
        },
      },
      ...(['auto', 'surround', 'stereo'] as OutputMode[]).map((m) => h('option', { value: m, selected: engine.output === m }, OUTPUT_LABEL[m])),
    );
    const volume = h('input', {
      type: 'range',
      min: 0,
      max: 1,
      step: 0.05,
      value: String(engine.volume),
      oninput: (e: Event) => sound.setVolume(Number((e.target as HTMLInputElement).value)),
    });
    const test = (label: string, ch: number) => h('button', { type: 'button', onclick: () => engine.testChannel(ch) }, label);
    return h(
      'div',
      { class: 'card' },
      h('h3', {}, '🎧 Výstup'),
      h(
        'p',
        { class: 'muted small', style: { margin: '0' } },
        engine.surroundCapable
          ? `Zařízení podporuje ${max} kanálů – hraje se ${engine.surroundActive ? 'prostorově v 5.1' : 've stereu'}.`
          : `Zařízení má ${max} kanály – zvuk 5.1 se smíchá do sterea. Pro 5.1 připoj receiver / reproduktory 5.1 (v systému nastav výstup 5.1).`,
      ),
      h('div', { class: 'grid2' }, h('label', {}, 'Režim', select), h('label', {}, 'Hlasitost', volume)),
      h('div', { class: 'small muted' }, 'Test reproduktorů:'),
      h(
        'div',
        { class: 'row wrap speaker-test' },
        test('L', CH.L),
        test('C', CH.C),
        test('R', CH.R),
        test('Rs', CH.Rs),
        test('Ls', CH.Ls),
        test('LFE', CH.LFE),
      ),
    );
  }

  // ---------- Zvuky & Akce ----------
  async function saveBinding(action: string, items: { soundId: string; volume: number }[]) {
    try {
      await api.setBinding(action, items);
      await lib.reload();
    } catch (e) {
      toast((e as Error).message, true);
    }
  }

  async function handleReplaceFile(s: SoundInfo, f: File) {
    if (f.size > SOUND_MAX_BYTES) {
      toast(`${f.name}: soubor je moc velký (max. 20 MB).`, true);
      return;
    }
    const ctx = engine.audio();
    if (!ctx) return toast('Prohlížeč nepodporuje Web Audio.', true);
    try {
      const buf = await ctx.decodeAudioData(await f.arrayBuffer());
      await api.replaceSoundFile(s.id, f, buf.numberOfChannels, buf.duration, f.name);
      lib.invalidate(s.id);
      toast(`Soubor pro „${s.name}“ byl nahrazen.`);
      await lib.reload();
    } catch (e) {
      toast(`${f.name}: ${(e as Error).message || 'nejde přehrát'}`, true);
    }
  }

  async function uploadForAction(a: SoundAction, files: File[]) {
    const ctx = engine.audio();
    if (!ctx) return toast('Prohlížeč nepodporuje Web Audio.', true);
    const curItems = [...lib.bindings(a.id)];
    let added = 0;
    for (const f of files) {
      if (f.size > SOUND_MAX_BYTES) {
        toast(`${f.name}: soubor je moc velký (max. 20 MB).`, true);
        continue;
      }
      try {
        const buf = await ctx.decodeAudioData(await f.arrayBuffer());
        const baseName = f.name.replace(/\.[^.]+$/, '');
        const soundName = curItems.length === 0 && added === 0 ? a.name : `${a.name} (${baseName})`;
        const s = await api.uploadSound(f, soundName, buf.numberOfChannels, buf.duration, f.name);
        curItems.push({ action: a.id, soundId: s.id, volume: 1 });
        added++;
      } catch (e) {
        toast(`${f.name}: ${(e as Error).message || 'nejde přehrát'}`, true);
      }
    }
    if (added > 0) {
      try {
        await api.setBinding(a.id, curItems);
        toast(added === 1 ? `Zvuk přiřazen k „${a.name}“.` : `Přidáno ${added} zvuků k „${a.name}“.`);
      } catch (e) {
        toast((e as Error).message, true);
      }
      await lib.reload();
    }
  }

  /** Vyzkoušet akci: náhodná varianta postupně zepředu, zleva, zprava a zezadu. */
  let testStep = 0;
  function tryAction(a: SoundAction) {
    const list = lib.bindings(a.id);
    const fb = !list.length && a.fallback ? lib.bindings(a.fallback) : [];
    const pick = (list.length ? list : fb)[Math.floor(Math.random() * (list.length || fb.length))];
    if (!pick) return toast('Akce nemá žádný zvuk.');
    const dirs = [0, -60, 60, 180];
    engine.preview(pick.soundId, { az: a.spatial ? dirs[testStep++ % dirs.length] : 0, volume: pick.volume });
  }

  function soundItem(a: SoundAction, b: { soundId: string; volume: number }): HTMLElement {
    const items = lib.bindings(a.id);
    const sounds = lib.data?.sounds ?? [];
    const snd = sounds.find((s) => s.id === b.soundId);
    if (!snd) return h('div', { class: 'action-sound-item muted small' }, 'Neznámý zvuk');

    let selectBtn: HTMLButtonElement | null = null;
    let fileInput: HTMLInputElement | null = null;

    if (admin) {
      fileInput = h('input', {
        type: 'file',
        accept: 'audio/*,.wav,.ogg,.flac,.mp3',
        hidden: true,
      }) as HTMLInputElement;
      fileInput.addEventListener('change', () => {
        const f = fileInput?.files?.[0];
        if (fileInput) fileInput.value = '';
        if (f) void handleReplaceFile(snd, f);
      });

      selectBtn = h(
        'button',
        {
          type: 'button',
          class: 'btn-select-sound',
          title: `Vybrat nový soubor z disku pro „${snd.name}“`,
          onclick: () => fileInput?.click(),
        },
        'Vybrat zvuk',
      ) as HTMLButtonElement;
    }

    const volVal = Math.round(b.volume * 100);
    const volLabel = h('span', { class: 'vol-num' }, `${volVal} %`);
    const volSlider = admin
      ? h('input', {
          type: 'range',
          min: 0,
          max: 2,
          step: 0.05,
          value: String(b.volume),
          title: `Hlasitost: ${volVal} %`,
          oninput: (e: Event) => {
            const val = Number((e.target as HTMLInputElement).value);
            volLabel.textContent = `${Math.round(val * 100)} %`;
          },
          onchange: (e: Event) => {
            const val = Number((e.target as HTMLInputElement).value);
            const next = items.map((x) => ({ soundId: x.soundId, volume: x.soundId === b.soundId ? val : x.volume }));
            void saveBinding(a.id, next);
          },
        })
      : null;

    const otherUses = (lib.data?.bindings ?? []).filter((x) => x.soundId === snd.id && x.action !== a.id);

    const nameEl = admin
      ? h('input', {
          class: 'sound-name-input',
          value: snd.name,
          maxlength: 80,
          title: 'Přejmenovat zvuk',
          onchange: async (e: Event) => {
            try {
              await api.renameSound(snd.id, (e.target as HTMLInputElement).value);
            } catch (err) {
              toast((err as Error).message, true);
            }
          },
        })
      : h('b', { class: 'sound-name-text' }, snd.name);

    const deleteBtn = admin
      ? h(
          'button',
          {
            type: 'button',
            class: 'icon-btn danger',
            title: otherUses.length > 0 ? 'Odebrat z této akce' : 'Smazat zvuk ze hry',
            onclick: async () => {
              if (otherUses.length > 0) {
                const otherNames = otherUses.map((x) => soundAction(x.action)?.name ?? x.action).join(', ');
                if (!confirm(`Odebrat zvuk „${snd.name}“ z akce „${a.name}“?\n\nZvuk zůstane zachován v: ${otherNames}.`)) return;
                await saveBinding(a.id, items.filter((x) => x.soundId !== snd.id));
              } else {
                if (!confirm(`Smazat zvuk „${snd.name}“?`)) return;
                try {
                  await api.deleteSound(snd.id);
                  await lib.reload();
                } catch (err) {
                  toast((err as Error).message, true);
                }
              }
            },
          },
          '🗑',
        )
      : null;

    return h(
      'div',
      { class: 'action-sound-item' },
      h('button', { type: 'button', class: 'icon-btn', title: 'Přehrát tento zvuk', onclick: () => engine.preview(snd.id, { volume: b.volume }) }, '▶'),
      h(
        'div',
        { class: 'sound-name-col' },
        nameEl,
        h(
          'div',
          { class: 'small muted sound-meta' },
          snd.fileName ? h('span', { class: 'sound-filename', title: `Soubor: ${snd.fileName}` }, `📄 ${snd.fileName}`) : null,
          otherUses.length ? h('span', { class: 'sound-used' }, `Použito také v: ${otherUses.map((x) => soundAction(x.action)?.name ?? x.action).join(', ')}`) : null,
        ),
      ),
      channelsBadge(snd.channels),
      h('span', { class: 'small muted' }, fmtDuration(snd.duration)),
      admin && volSlider
        ? h('div', { class: 'action-sound-vol', title: 'Hlasitost' }, h('span', {}, '🔉'), volSlider, volLabel)
        : null,
      selectBtn,
      fileInput,
      deleteBtn,
    );
  }

  function actionRow(a: SoundAction): HTMLElement {
    const items = lib.bindings(a.id);
    const sounds = lib.data?.sounds ?? [];
    const fallback = !items.length && a.fallback ? soundAction(a.fallback)?.name : null;

    let actionFileInput: HTMLInputElement | null = null;
    let uploadBtn: HTMLElement | null = null;
    let defaultGenBtn: HTMLElement | null = null;

    if (admin) {
      actionFileInput = h('input', {
        type: 'file',
        accept: 'audio/*,.wav,.ogg,.flac,.mp3',
        multiple: true,
        hidden: true,
      }) as HTMLInputElement;
      actionFileInput.addEventListener('change', () => {
        if (actionFileInput?.files?.length) void uploadForAction(a, [...actionFileInput.files]);
        if (actionFileInput) actionFileInput.value = '';
      });

      uploadBtn = h(
        'button',
        {
          type: 'button',
          class: 'btn-action-upload',
          title: 'Nahrát zvukový soubor přímo pro tuto akci (i přetažením myší)',
          onclick: () => actionFileInput?.click(),
        },
        '📁 Nahrát zvuk',
      );

      if (!items.length && hasDefaultSound(a.id)) {
        defaultGenBtn = h(
          'button',
          {
            type: 'button',
            class: 'btn-action-default ghost',
            title: 'Vygenerovat výchozí syntetizovaný zvuk pro tuto akci',
            onclick: async () => {
              if (defaultGenBtn) (defaultGenBtn as HTMLButtonElement).disabled = true;
              try {
                await generateDefaultSound(a.id);
                toast(`Vygenerován výchozí zvuk pro „${a.name}“.`);
                await lib.reload();
              } catch (err) {
                toast((err as Error).message, true);
              }
            },
          },
          '✨ Výchozí syntéza',
        );
      }
    }

    const availableSounds = sounds.filter((s) => !items.some((b) => b.soundId === s.id));
    const addExisting = admin && availableSounds.length > 0
      ? h(
          'select',
          {
            class: 'add-sound',
            onchange: async (e: Event) => {
              const id = (e.target as HTMLSelectElement).value;
              if (!id) return;
              await saveBinding(a.id, [...items.map((x) => ({ soundId: x.soundId, volume: x.volume })), { soundId: id, volume: 1 }]);
            },
          },
          h('option', { value: '' }, '➕ Přiřadit existující zvuk…'),
          ...availableSounds.map((s) =>
            h('option', { value: s.id }, s.fileName && s.fileName !== s.name ? `${s.name} (${s.fileName})` : s.name),
          ),
        )
      : null;

    const row = h(
      'div',
      {
        class: `action-row${items.length ? '' : ' empty'}`,
        ...(admin
          ? {
              ondragover: (e: DragEvent) => {
                e.preventDefault();
                row.classList.add('drag-over');
              },
              ondragleave: () => row.classList.remove('drag-over'),
              ondrop: (e: DragEvent) => {
                e.preventDefault();
                row.classList.remove('drag-over');
                if (e.dataTransfer?.files?.length) void uploadForAction(a, [...e.dataTransfer.files]);
              },
            }
          : {}),
      },
      h(
        'div',
        { class: 'action-head' },
        h('span', { class: 'action-title' }, a.name),
        h(
          'span',
          { class: 'small muted' },
          [a.spatial ? '🔊 prostorově' : '🔈 zepředu', a.loop ? '🔁 smyčka' : '', a.lfe >= 0.5 ? '💥 LFE' : ''].filter(Boolean).join(' · '),
        ),
        h('button', { type: 'button', class: 'icon-btn', title: 'Vyzkoušet v prostoru', onclick: () => tryAction(a) }, '▶'),
      ),
      items.length
        ? h('div', { class: 'action-sounds' }, ...items.map((b) => soundItem(a, b)))
        : h('div', { class: 'small muted empty-note' }, fallback ? `bez zvuku – použije se „${fallback}“` : 'bez zvuku (ticho)'),
      admin
        ? h(
            'div',
            { class: 'action-controls' },
            uploadBtn,
            defaultGenBtn,
            addExisting,
            actionFileInput,
          )
        : null,
    );

    return row;
  }

  function actionsCard(): HTMLElement {
    const sounds = lib.data?.sounds ?? [];
    const bindings = lib.data?.bindings ?? [];
    const missing = SOUND_ACTIONS.filter((a) => !lib.bindings(a.id).length).length;

    const usedIds = new Set(bindings.map((b) => b.soundId));
    const unusedSounds = sounds.filter((s) => !usedIds.has(s.id));

    let genBtn: HTMLButtonElement | null = null;
    if (admin && missing > 0) {
      genBtn = h('button', { type: 'button', class: 'btn-gen-all' }, `✨ Vygenerovat výchozí sadu (${missing} akcí bez zvuku)`) as HTMLButtonElement;
      genBtn.addEventListener('click', async () => {
        if (!genBtn) return;
        genBtn.disabled = true;
        try {
          const n = await generateDefaults(
            (act) => lib.bindings(act).length > 0,
            (done, total) => {
              if (genBtn) genBtn.textContent = `Generuji… ${done}/${total}`;
            },
          );
          toast(`Vygenerováno ${n} zvuků.`);
        } catch (e) {
          toast((e as Error).message, true);
        }
        await lib.reload();
      });
    }

    let cleanupBtn: HTMLButtonElement | null = null;
    if (admin && unusedSounds.length > 0) {
      cleanupBtn = h(
        'button',
        {
          type: 'button',
          class: 'ghost danger small',
          title: 'Smazat nepoužívané audio soubory ze serveru',
          onclick: async () => {
            if (!confirm(`Smazat ${unusedSounds.length} nepoužívaných souborů ze serveru?`)) return;
            try {
              for (const s of unusedSounds) {
                await api.deleteSound(s.id);
              }
              toast(`Smazáno ${unusedSounds.length} nepoužívaných souborů.`);
              await lib.reload();
            } catch (e) {
              toast((e as Error).message, true);
            }
          },
        },
        `🧹 Smazat ${unusedSounds.length} nepoužívaných souborů`,
      ) as HTMLButtonElement;
    }

    return h(
      'div',
      { class: 'card' },
      h(
        'div',
        { class: 'actions-card-header' },
        h('h3', {}, `🎬 Herní akce a zvuky (${SOUND_ACTIONS.length})`),
        h(
          'p',
          { class: 'muted small', style: { margin: '0' } },
          'Každá akce může mít jeden nebo více zvuků (při více zvucích se náhodně střídají). Soubory lze nahrát přímo k akci nebo na ni přetáhnout myší.',
        ),
        admin && (genBtn || cleanupBtn)
          ? h('div', { class: 'row wrap gap', style: { marginTop: '8px' } }, genBtn, cleanupBtn)
          : null,
        admin
          ? null
          : h(
              'p',
              { class: 'muted small', style: { margin: '4px 0 0 0' } },
              'Nahrávat a upravovat zvuky smí jen správci (proměnná ADMINS na serveru).',
            ),
      ),
      ...SOUND_GROUPS.map((g) => {
        const groupActions = SOUND_ACTIONS.filter((a) => a.group === g.id);
        const totalSounds = groupActions.reduce((acc, a) => acc + lib.bindings(a.id).length, 0);
        return h(
          'details',
          { class: 'action-group', open: g.id !== 'emote' },
          h(
            'summary',
            {},
            h('span', { class: 'group-title' }, g.name),
            h('span', { class: 'small muted group-count' }, `(${groupActions.length} akcí, ${totalSounds} zvuků)`),
          ),
          ...groupActions.map((a) => actionRow(a)),
        );
      }),
    );
  }

  function render() {
    const open = new Set([...body.querySelectorAll('details.action-group')].filter((d) => (d as HTMLDetailsElement).open).map((d) => d.querySelector('.group-title')?.textContent));
    const hadGroups = body.querySelector('details.action-group') !== null;
    body.replaceChildren(outputCard(), actionsCard());
    if (hadGroups) {
      for (const d of body.querySelectorAll('details.action-group')) {
        const title = d.querySelector('.group-title')?.textContent;
        (d as HTMLDetailsElement).open = open.has(title);
      }
    }
  }

  const el = h(
    'div',
    { class: 'backdrop' },
    h(
      'div',
      { class: 'page' },
      h(
        'div',
        { class: 'topbar' },
        h('button', { class: 'ghost', onclick: () => go('/') }, '← Lobby'),
        h('div', { class: 'logo small-logo' }, '🔊 Zvuky'),
      ),
      body,
    ),
  );

  const off = lib.onChange(render);
  render();
  void lib.load().then(render);

  return {
    el,
    destroy() {
      off();
    },
  };
}
